import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    if (!token) { toast.error("Missing reset token"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset · you can sign in now");
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="p-8 lg:p-16 grid place-items-center bg-background order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground" data-testid="reset-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
          <h1 className="font-semibold tracking-tight text-4xl mt-6">Set new password.</h1>
          <p className="text-sm text-muted-foreground mt-2">Pick something 8+ characters you can actually remember.</p>

          {!token ? (
            <div className="mt-8 border border-amber-300 bg-amber-50 p-5" data-testid="reset-no-token">
              <p className="text-sm text-amber-900">No reset token in this URL. <Link to="/forgot-password" className="underline">Request a new reset link.</Link></p>
            </div>
          ) : done ? (
            <div className="mt-8 border border-emerald-300 bg-emerald-50 p-5" data-testid="reset-done">
              <CheckCircle2 className="w-7 h-7 text-emerald-700" strokeWidth={1.5} />
              <h3 className="font-semibold tracking-tight text-xl mt-3">Done.</h3>
              <p className="text-sm text-emerald-900/90 mt-2">Sending you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
              <div className="space-y-2">
                <Label className="font-mono-data text-xs uppercase tracking-widest">New password</Label>
                <Input required type="password" value={pw} onChange={(e)=>setPw(e.target.value)} minLength={8}
                  className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground" data-testid="reset-pw-input" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono-data text-xs uppercase tracking-widest">Confirm password</Label>
                <Input required type="password" value={pw2} onChange={(e)=>setPw2(e.target.value)} minLength={8}
                  className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground" data-testid="reset-pw2-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="reset-submit">
                <KeyRound className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Reset password"}
              </Button>
            </form>
          )}
        </div>
      </div>
      <div className="hidden lg:flex items-center justify-center p-16 bg-foreground text-background order-1 lg:order-2">
        <div className="max-w-sm">
          <p className="font-mono-data text-xs uppercase tracking-widest text-background/60">// new credential</p>
          <h2 className="font-semibold tracking-tight text-5xl mt-3">Fresh start.</h2>
        </div>
      </div>
    </div>
  );
}
